import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import {
  BRAND_DEPLOYMENT_CARD,
  BRAND_DEPLOYMENT_CARD_EDGE,
  BRAND_DEPLOYMENT_CARD_STATUS,
  BRAND_DEPLOYMENT_CARD_DOT_GLOW,
  BRAND_DEPLOYMENT_DOTS_ANIM,
  BRAND_DEPLOYMENT_GLOW,
  BRAND_DEPLOYMENT_GLOW_STOPS,
  REDUCED_MOTION,
  type DeploymentStepState,
} from '../../../../../theme/tokens';
import { CardShell } from '../cardBase.styles';

/** Registers the animatable border-glow angle once (rendered a single time by
 *  ScrollStrip). On engines that ignore animating a registered property the arc
 *  simply sits static — acceptable. */
export const GlowAngleProperty = createGlobalStyle`
  @property --deployment-glow-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }
`;

/** One loop of the glow travelling around the card edge. */
const glowSpin = keyframes`
  to {
    --deployment-glow-angle: 360deg;
  }
`;

// A conic gradient whose `from <angle>` is an ANIMATED CSS custom property — a
// sweeping coloured arc; cannot be a static GRADIENTS token.
const GLOW_RING = `conic-gradient(from var(--deployment-glow-angle), transparent 0 ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[0]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.62}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[1]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.36}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[2]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.09}%, transparent 100%)`; // design-allow
// Mask-composite alpha idiom that cuts the gradient down to a ring; the `#000` is a
// structural alpha source, not a colour.
const RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)'; // design-allow

/**
 * The deploy-step card — Figma node 9103:92993. No border: an artwork background
 * fills the card, legibility scrims top + bottom, a "STEP n OF total" eyebrow + the
 * step title up top, and a status footer at the bottom — a determinate progress ring
 * + live percent while in-progress, a coloured dot + label otherwise.
 *
 * The in-progress card also carries the travelling edge-glow (gated by `data-glow`):
 * a masked conic ring hugging the card's rounded edge (a soft GLOW, not a border),
 * clipped to the card so it reads as a glowing rim over the artwork.
 */
export const Card = styled(CardShell)`
  padding: 16px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 4;
    border-radius: inherit;
    padding: 1.5px;
    background: ${GLOW_RING};
    -webkit-mask: ${RING_MASK};
    mask: ${RING_MASK};
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    filter: blur(2.5px);
    opacity: 0;
    transition: opacity ${BRAND_DEPLOYMENT_GLOW.FADE_MS}ms ease;
    pointer-events: none;
  }

  &[data-glow='on']::before {
    opacity: ${BRAND_DEPLOYMENT_GLOW.ALPHA};
    animation: ${glowSpin} ${BRAND_DEPLOYMENT_GLOW.SPIN_MS}ms linear infinite;
  }

  /* Hairline matching the section shell's own border, LIGHT theme only: the token is a
     fully transparent colour on dark, so this paints nothing there and the card stays
     borderless as designed. An overlay ring rather than a real border, because the
     artwork fills inset:0 and would cover an inset shadow while a real border would
     inset the artwork (changing dark too). Above the content (z2), below the
     in-progress glow ring (z4), and it keeps the card's own 24px radius. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 3;
    border-radius: inherit;
    border: 1px solid ${BRAND_DEPLOYMENT_CARD_EDGE};
    pointer-events: none;
  }

  ${REDUCED_MOTION} {
    &[data-glow='on']::before {
      animation: none;
    }
  }
`;

/** Fixed placeholder artwork (the future per-step visual swaps in here). */
export const Artwork = styled.img`
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

export const ScrimTop = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 96px;
  z-index: 1;
  pointer-events: none;
  background: ${BRAND_DEPLOYMENT_CARD.scrimTop};
`;

export const ScrimBottom = styled.span`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 118px;
  z-index: 1;
  pointer-events: none;
  background: ${BRAND_DEPLOYMENT_CARD.scrimBottom};
`;

/** Top block: grows so the footer is pushed to the card's bottom edge. */
export const Header = styled.div`
  position: relative;
  z-index: 2;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  padding-right: 24px;
`;

/** Title line — a numeric index ("1.") prefixing the step's friendly name. Replaces the
 *  retired "STEP n OF total" eyebrow with inline numbering. */
export const CardTitle = styled.span`
  font-size: 13px;
  line-height: 1.43;
  font-weight: 500;
  color: ${BRAND_DEPLOYMENT_CARD.title};
  word-break: break-word;
`;

/** The "{n}." index leading the title — carries the muted tone of the retired eyebrow. */
export const TitleIndex = styled.span`
  color: ${BRAND_DEPLOYMENT_CARD.eyebrow};
  font-variant-numeric: tabular-nums;
`;

export const Footer = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** Soft pulse on the in-progress orb (the Figma glowing dot). */
const dotPulse = keyframes`
  0%,
  100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.18);
    opacity: 1;
  }
`;

export const StatusDot = styled.span<{ $state: DeploymentStepState }>`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${(p) => BRAND_DEPLOYMENT_CARD_STATUS[p.$state].dot};

  ${(p) =>
    p.$state === 'active' &&
    css`
      box-shadow: 0 0 10px 1px ${BRAND_DEPLOYMENT_CARD_DOT_GLOW};
      animation: ${dotPulse} ${BRAND_DEPLOYMENT_DOTS_ANIM.PULSE_MS}ms ease-in-out infinite;
    `}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const StatusLabel = styled.span<{ $state: DeploymentStepState }>`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  line-height: 1.34;
  font-weight: 400;
  color: ${(p) => BRAND_DEPLOYMENT_CARD_STATUS[p.$state].text};
  white-space: nowrap;
`;
